import { useState } from 'react'
import { useMutation } from '@apollo/client'
import { Link } from 'react-router-dom'
import { CREATE_ORDER } from '../../graphql/operations'
import { useCart } from '../../lib/cart'
import { useAuth } from '../../auth/AuthContext'
import { formatPrice } from '../../lib/format'
import StripeCheckout, { type PaidOrder } from './StripeCheckout'

export default function Checkout() {
  const { items, total, currency, refresh } = useCart()
  const { user, token } = useAuth()
  const [order, setOrder] = useState<PaidOrder | null>(null)

  function complete(o: PaidOrder) {
    setOrder(o)
    void refresh() // cart consumed into the order
  }

  if (order) {
    return (
      <div className="checkout-done" data-testid="order-confirmation">
        <div className="checkout-tick">✓</div>
        <h1>Order placed</h1>
        <p className="muted">A confirmation is on its way to {order.email}.</p>
        <p className="checkout-ordernum">
          Order <strong data-testid="order-number">{order.orderNumber}</strong>
        </p>
        <p className="checkout-ordertotal">{formatPrice(order.total, order.currency)}</p>
        <Link to="/shop" className="btn-primary">Continue shopping</Link>
      </div>
    )
  }

  if (items.length === 0) {
    return (
      <div className="cart-empty">
        <h1>Nothing to check out</h1>
        <Link to="/shop" className="btn-primary">Shop the collection</Link>
      </div>
    )
  }

  return (
    <div className="checkout" data-testid="checkout">
      <h1 className="shop-page-title">Checkout</h1>

      <div className="checkout-summary checkout-summary--top">
        <span>{items.length} item{items.length === 1 ? '' : 's'}</span>
        <strong>{formatPrice(total, currency)}</strong>
      </div>

      {token ? (
        // Signed-in: real Stripe card payment.
        <StripeCheckout onPaid={complete} />
      ) : (
        // Guest: express place-order (unpaid). Sign in to pay by card.
        <GuestCheckout onPlaced={complete} email={user?.email ?? ''} />
      )}
    </div>
  )
}

// ---- Guest express checkout (unpaid order) ----------------------------
function GuestCheckout({ onPlaced, email: initialEmail }: { onPlaced: (o: PaidOrder) => void; email: string }) {
  const { guestToken, total, currency } = useCart()
  const [email, setEmail] = useState(initialEmail)
  const [name, setName] = useState('')
  const [address1, setAddress1] = useState('')
  const [city, setCity] = useState('')
  const [postcode, setPostcode] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [createOrder, { loading }] = useMutation(CREATE_ORDER)

  async function placeOrder(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    try {
      const { data } = await createOrder({
        variables: { guestToken, email, shippingAddress: { name, address1, city, postcode } },
      })
      const payload = data?.createOrder
      const err = payload?.userErrors?.[0]?.message
      if (err) { setError(err); return }
      if (payload?.order) onPlaced(payload.order)
      else setError('Order could not be created.')
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
    }
  }

  return (
    <form onSubmit={placeOrder} className="checkout-form">
      <p className="checkout-signin-hint">
        Checking out as a guest. <Link to="/login">Sign in</Link> to pay by card.
      </p>
      <label>
        <span>Email (for your receipt)</span>
        <input type="email" required value={email} data-testid="checkout-email"
          onChange={(e) => setEmail(e.target.value)} autoComplete="email" />
      </label>
      <label>
        <span>Full name</span>
        <input required value={name} onChange={(e) => setName(e.target.value)} autoComplete="name" />
      </label>
      <label>
        <span>Address</span>
        <input required value={address1} onChange={(e) => setAddress1(e.target.value)} autoComplete="address-line1" />
      </label>
      <div className="checkout-row">
        <label>
          <span>City</span>
          <input required value={city} onChange={(e) => setCity(e.target.value)} autoComplete="address-level2" />
        </label>
        <label>
          <span>Postcode</span>
          <input required value={postcode} onChange={(e) => setPostcode(e.target.value)} autoComplete="postal-code" />
        </label>
      </div>
      {error && <p className="error" data-testid="checkout-error">{error}</p>}
      <button type="submit" className="btn-primary checkout-place" data-testid="place-order" disabled={loading}>
        {loading ? 'Placing order…' : `Place order — ${formatPrice(total, currency)}`}
      </button>
      <p className="checkout-note">Guest checkout creates an unpaid order. No card is charged.</p>
    </form>
  )
}
